import Events from '@utilities/events'
import API from '@utilities/api'
import moduleInit from '@utilities/module-init'
import keyboardEventCode from '@utilities/keyboard-event-code'
import { body } from '@utilities/dom-elements'
import Modal from './modal'

const MODAL_CONTENT_HOOK = '[js-hook-modal-content]'
const MODAL_HTML_CLASS = 'is--modal-open'

class AjaxModal extends Modal {
  constructor() {
    super()
    this.href = ''
  }

  /**
   * Bind all modal specific events
   * @param {string} id Modal id
   * @param {HTMLElement} triggerBtn Button to open modal
   * @param {HTMLElement} closeBtn Button to close modal
   */
  bindModalEvents({ el, id, triggerBtn, closeBtn }) {
    this.bindTriggerButton({ el, id, triggerBtn })

    if (el.dataset.modalHasTransform) {
      el.addEventListener('transitionend', () => Modal.hideModal(el))
    }

    Events.$on(`modal[${id}]::setContentAndOpen`, (_, data) =>
      this.setModalContentAndOpen(data),
    )
    Events.$on(`modal[${id}]::load`, () => this.loadModal({ id, href: this.href }))
    Events.$on(`modal[${id}]::close`, () => this.closeModal({ id }))
    Events.$on(`modal[${id}]::open`, () => this.openModal({ id }))

    Modal.bindCloseButton({ el, id, closeBtn })

    // Close on ESCAPE_KEY
    document.addEventListener('keyup', (event) => {
      if (keyboardEventCode.escape.indexOf(event.keyCode || event.key)  !== -1 && body.classList.contains(MODAL_HTML_CLASS)) {
        Events.$trigger('modal::close')
        Events.$trigger(`modal[${id}]::close`, { data: { id } })
      }
    })
  }

  /**
   * Bind all button specific events
   * @param {string} id Modal id
   * @param {HTMLElement} triggerBtn Button to open modal
   */
  bindTriggerButton({ el, id, triggerBtn }) {
    triggerBtn.forEach((triggerEl) => {
      triggerEl.addEventListener('click', (clickEvent) => {
        if (el.modalIsOpen) {
          Events.$trigger('modal::close', { data: { id } })
          Events.$trigger(`modal[${id}]::close`, { data: { id } })
        } else if (clickEvent.currentTarget && clickEvent.currentTarget.getAttribute('href')) {
          this.href = clickEvent.currentTarget.getAttribute('href')
          Events.$trigger('modal::load', {
            data: { id, href: this.href },
          })
        } else {
          Events.$trigger('modal::open', { data: { id } })
          Events.$trigger(`modal[${id}]::open`, { data: { id } })
        }
      })

      if (triggerEl.dataset.open) triggerEl.click()
    })
  }

  bindEvents() {
    Modal.prototype.bindEvents.call(this)

    Events.$on('modal::load', (event, data) => this.loadModal(data))
  }

  /**
   * Process Fetch error for _getContent
   *
   * @param {Object|string} error
   */
  getContentError() {
    this.isLoading = false
    Events.$trigger('loader::hide')
  }

  /**
   * Parse Fetch results and updated select DOM elements
   *
   * @param {string} response - Fetch response HTML code
   * @return {undefined}
   */
  getContentSuccess(response, id) {
    this.isLoading = false
    Events.$trigger('loader::hide')
    Events.$trigger(`modal[${id}]::setContentAndOpen`, { data: { id, htmlString: response } })
  }

  setModalContentAndOpen(data) {
    const { id, htmlString, type } = data
    const parser = new DOMParser()
    const content = parser.parseFromString(htmlString, 'text/html').querySelector(MODAL_CONTENT_HOOK)

    if (!content) return;
    // Get current modal from all known modals
    const modal = this.registeredModals[`modal-${id}`]

    // If there is no modal do nothing
    if (!modal || !modal.el) return
    this.modalElement = modal.el

    if(type)
      this.setModalTypeClasses(type)

    const modalContent = modal.el.querySelector(MODAL_CONTENT_HOOK)
    if (!modalContent) return

    modalContent.parentNode.replaceChild(content, modalContent)


    Events.$trigger('modal::bind-buttons', { data: { id } })
    Events.$trigger('lazyimage::update')
    Events.$trigger('events::dom-reinit')
    Events.$trigger('modal::open', { data: { id } })
    Events.$trigger(`modal[${id}]::open`, { data: { id } })

    this.isLoading = false
    Events.$trigger('loader::hide')

    // @TODO create extends of ajax modal to make this part below custom
    moduleInit.async('[js-hook-ajax-modal-button]', () =>
      import(/* webpackChunkName: "js/AjaxModalButton" */ '@components/ajax-modal-button'),
    )
    moduleInit.async('[js-hook-add-to-cart]', () =>
      import(/* webpackChunkName: "js/AddToCart" */ '@components/add-to-cart')
    )
    moduleInit.async('[js-hook-accordion]', () =>
      import(/* webpackChunkName: "js/Accordion" */ '@components/accordion'),
    )
    moduleInit.async('[js-hook-address-form]', () =>
      import(/* webpackChunkName: "js/AddressForm" */ '@components/address-form'),
    )
    moduleInit.async('[js-hook-product-attribute]', () =>
      import(/* webpackChunkName: "js/ProductAttribute" */ '@components/product-attribute'),
    )
    moduleInit.async('[js-hook-product-attribute-size]', () =>
      import(/* webpackChunkName: "js/ProductAttributeSize" */ '@components/product-attribute/size'),
    )
    moduleInit.async('[js-hook-toggle]', () =>
      import(/* webpackChunkName: "js/Toggle" */ '@components/toggle'),
    )
    moduleInit.async('[js-hook-alert]', () =>
      import(/* webpackChunkName: "js/Alert" */ '@components/alert'),
    )
  }

  removeCurrentModalModifiers() {
    const prefix = 'modal--';
    const classes = this.modalElement.className.split(" ").filter(c => !c.startsWith(prefix));
    this.modalElement.className = classes.join(" ").trim();
  }

  /**
   * Control css-modifier classes for modals based on Type
   *
   * Type-list:
   * no type/default; Centered modal
   * 'flyout'; Panel animates in from the right side on desktop. Animates in from the bottom on mobile.
   */
  setModalTypeClasses(modalType) {
    this.removeCurrentModalModifiers();
    this.modalElement.classList.add(`modal--${modalType}`)
  }

  /**
   * Retrieve data from tab content search results
   */
  loadModal(data) {
    const { id, href } = data
    this.isLoading = true
    Events.$trigger('loader::show')

    API.get(href)
      .then((response) => this.getContentSuccess(response.data, id))
      .catch((error) => this.getContentError(error.response.data || ''))
  }
}

export default new AjaxModal()
